import type { AssetProcessingActivityReviewRow } from 'config/api/ropaReviews/ropaReviews.types';

export type DpiaAdminRowActionContext = Pick<AssetProcessingActivityReviewRow, 'dpiaDisplayStatus' | 'dpiaRequestStatus' | 'dpiaId'>;

export const canShowDpiaHistoryAction = (row: Pick<AssetProcessingActivityReviewRow, 'hasPreviousDpias' | 'dpiaCount'>): boolean =>
  Boolean(row.hasPreviousDpias || (row.dpiaCount ?? 0) > 1);

export const canStartDpiaReassessment = (status: string | undefined, canEdit = true): boolean => canEdit && status === 'decision_made';

const DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES = ['indicated', 'dpia_required'] as const;

const APA_REVIEW_ALLOWED_DPIA_DISPLAY_STATUSES = new Set(['not_indicated', 'dpia_not_required', 'not_required']);

export const isDpiaRequestAwaitingRaAssessment = (ctx: DpiaAdminRowActionContext): boolean =>
  ctx.dpiaRequestStatus === 'draft' || ctx.dpiaRequestStatus === 'indicated';

export const isDpiaIndicationAwaitingRaAssessment = (ctx: DpiaAdminRowActionContext): boolean =>
  DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES.includes(
    ctx.dpiaDisplayStatus as typeof DPIA_INDICATION_AWAITING_RA_ASSESSMENT_DISPLAY_STATUSES[number],
  );

export const isDpiaFormAwaitingAdminReview = (ctx: DpiaAdminRowActionContext): boolean =>
  ['ready_for_review', 'clarification_requested'].includes(ctx.dpiaDisplayStatus);

export const shouldShowApaReviewRowActions = (ctx: DpiaAdminRowActionContext): boolean => {
  if (isDpiaRequestAwaitingRaAssessment(ctx) || isDpiaFormAwaitingAdminReview(ctx)) {
    return false;
  }

  if (!ctx.dpiaDisplayStatus) {
    return true;
  }

  return APA_REVIEW_ALLOWED_DPIA_DISPLAY_STATUSES.has(ctx.dpiaDisplayStatus);
};

export const shouldShowOpenAdminDpiaReview = (ctx: DpiaAdminRowActionContext): boolean =>
  Boolean(ctx.dpiaId) && isDpiaFormAwaitingAdminReview(ctx);

export const getAdminDpiaNeedAssessmentAction = (ctx: DpiaAdminRowActionContext, canAssessDpia: boolean): 'reassess' | 'assess' | null => {
  if (!canAssessDpia) {
    return null;
  }

  if (isDpiaFormAwaitingAdminReview(ctx)) {
    return null;
  }

  if (ctx.dpiaDisplayStatus === 'decision_made') {
    return 'reassess';
  }

  if (isDpiaRequestAwaitingRaAssessment(ctx)) {
    return 'assess';
  }

  if (isDpiaIndicationAwaitingRaAssessment(ctx)) {
    return 'assess';
  }

  return null;
};
